import FilterBox from '../filter-box'
import FilterBase from '../filter-base';
import { FilterActiveValue } from '../active-filters';
import { ColorOption, ColorChildOption } from './color-option';
import FilterParamStats from '../filter-stats';
import { FilterParamItem } from '../filter-param-items';
import { hide, show } from '../../common';

export default class ColorFilter extends FilterBase {

    private options: ColorOption[] = [];
    private isHandlingSelectionUpdate: boolean;
    private itemsWrapper: HTMLElement;

    constructor(element: HTMLElement, filterBox: FilterBox) {
        super(element, filterBox);
        this.itemsWrapper = this.baseElement.querySelector('[data-component-part="top-options-wrapper"]');
    }

    init(): void {
        const elements = document.querySelectorAll('.color-value, .image-icon');
        const htmlElements = [...elements].map(e => e as HTMLElement);
        this.options = htmlElements.map(e => new ColorOption(this.paramId, this.pageId, e.title, e.dataset.listId, e, () => this.onSelectionUpdate()));
    }

    protected async setValuesInternal(values: string): Promise<void> {
        const selectedIds = values.split(',');

        const selectedTopLevelItems = this.options.filter(o => selectedIds.some(id => id === o.listId));
        for (let i = 0; i < selectedTopLevelItems.length; i++) {
            await selectedTopLevelItems[i].select(false);
        }

        this.options.forEach(o => o.children
            .filter(o => selectedIds.some(id => id === o.listId))
            .forEach(i => i.select()));
    }

    unselect(value: any): void {
        const topLevelItem = this.options.find(o => o.listId === value);
        if (topLevelItem) {
            topLevelItem.unselect();
        } else {
            const subColor = this.getChildColorByListId(value);
            subColor.unselect();
        }

        this.onSelectionUpdate();
    }

    protected clearValuesInternal(): void {
        this.options.forEach(o => o.unselect());
    }

    protected clear(): void {
        this.clearValues();
        this.applyFilterChanges();
    }

    private getChildColorByListId(listId: string): ColorChildOption {
        for (let i = 0; i < this.options.length; i++) {
            const subcolor = this.options[i].children.find(o => o.listId === listId);
            if (subcolor) return subcolor;
        }
        return null;
    }

    protected getActiveFilterValues(): FilterActiveValue[] {
        let selectedValues: FilterActiveValue[] = [];
        this.options.forEach(o => {
            const selectedChildren = o.children
                .filter(s => s.isSelected)
                .map(s => <FilterActiveValue>{ value: s.listId, displayName: s.name });
            if (selectedChildren && selectedChildren.length > 0) {
                selectedValues = selectedValues.concat(selectedChildren);
            }
        })

        return selectedValues;
    }

    private onSelectionUpdate(): void {
        if (this.isHandlingSelectionUpdate)
            return;
        this.isHandlingSelectionUpdate = true;

        try {
            this.unselectParentsWithoutSelectedChildren();
            this.updateAttributeAndApplyChanges();
        }
        finally {
            this.isHandlingSelectionUpdate = false;
        }
    }

    private unselectParentsWithoutSelectedChildren() {
        this.options
            .filter(x => x.isSelected && !x.hasSelectedChildren() && x.hasChildren())
            .forEach(x => x.unselect());
    }


    protected getSelectedValuesConcat(): string {
        return this.getSelectedValues().join(',');
    }

    private getSelectedValues(): string[]{
        let selectedValues: string[] = [];
        this.options.forEach(o => {
            if (o.isSelected) {
                selectedValues.push(o.listId);
            }
            const selectedChildren = o.children
                .filter(s => s.isSelected)
                .map(s => s.listId);
            if (selectedChildren) {
                selectedValues = selectedValues.concat(selectedChildren);
            }
        });

        return selectedValues;
    }

    public setStats(stats: FilterParamStats): void {
        const items = stats.Items;
        
        if(!items || items.length === 0){
            hide(this.baseElement);
            return;
        }
        show(this.baseElement);

        this.options.forEach(o => o.updateItems(items));

        const itemsToAdd = items.filter(i => !this.options.find(o => o.listId === i.ListId.toString()));
        this.addOptions(itemsToAdd);

        this.updateActiveFilterValues();
    }

    private addOptions(items: FilterParamItem[]){
        const optionsToAdd = items
            .map(i => ColorOption.CreateOptionElement(i))
            .map(e => new ColorOption(this.paramId, this.pageId, e.title, e.dataset.listId, e, () => this.onSelectionUpdate()));

        if(optionsToAdd.length !== 0){
            this.options = this.options.concat(optionsToAdd);
            this.sortOptionElements();
        }
    }

    private sortOptionElements() {
        while (this.itemsWrapper.firstChild) {
            this.itemsWrapper.removeChild((this.itemsWrapper.lastChild));
        }

        const sortedOptions = this.options.sort((a,b) => a.sortValue - b.sortValue);
        sortedOptions.forEach(o => this.itemsWrapper.appendChild(o.element));
    }


    private concatValues(selectedValues: FilterActiveValue[]): string {
        return selectedValues.map(v => v.value).join(',');
    }
}

